import { isDefined } from "@clipboard-health/util-ts";

const SESSION_ID_STORAGE_KEY = "ratingPromptSessionId";

// Cached per JS context to skip repeat storage reads and stay stable if
// `sessionStorage` access throws.
let inMemorySessionId: string | undefined;

/**
 * Stable id for the current app session, used to build the rating-prompt event
 * idempotency key. First-party on purpose — a Datadog RUM session id would be
 * dropped by the same ad blockers these events route around. `sessionStorage`
 * scopes it to one session: remounts and StrictMode double-fires reuse it (the
 * backend's unique index dedupes), a new session mints a fresh id (a re-shown
 * prompt records a new row).
 */
export function getRatingPromptSessionId(): string {
  if (isDefined(inMemorySessionId)) {
    return inMemorySessionId;
  }

  try {
    const stored = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (isDefined(stored) && stored.length > 0) {
      inMemorySessionId = stored;
      return stored;
    }

    const minted = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, minted);
    inMemorySessionId = minted;
    return minted;
  } catch {
    // `sessionStorage` throws in private-mode / sandboxed contexts; fall back
    // to a module-scoped id.
    inMemorySessionId ??= crypto.randomUUID();
    return inMemorySessionId;
  }
}
